import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, Shield, Building2, Plus, Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';

// Reflects the original schema from the screenshot
interface UserProfile {
  id: string;
  email: string;
  role: 'admin' | 'manager' | 'user';
  company_ids: string[];
  is_active: boolean;
}

interface Company {
  id: number;
  name: string;
  logo_url?: string;
}

interface AdminSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const AdminSettingsDialog: React.FC<AdminSettingsDialogProps> = ({ open, onOpenChange }) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', role: 'user' as 'user' | 'manager' });
  
  const companyMap = new Map<string, string>();
  companies.forEach(c => companyMap.set(String(c.id), c.name));

  useEffect(() => {
    if (open) fetchInitialData();
  }, [open]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [usersRes, companiesRes] = await Promise.all([
        supabase.from('profiles').select('id, email, role, company_ids, is_active'),
        supabase.from('companies').select('id, name, logo_url')
      ]);
      if (usersRes.error) throw usersRes.error;
      if (companiesRes.error) throw companiesRes.error;
      setUsers(usersRes.data.map(u => ({ ...u, role: u.role as any, company_ids: u.company_ids || [] })));
      setCompanies(companiesRes.data || []);
    } catch (error: any) { toast.error(`資料載入失敗: ${error.message}`); } 
    finally { setLoading(false); }
  };

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.password) { toast.error('請填寫所有欄位'); return; }
    setLoading(true);
    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({ email: newUser.email, password: newUser.password });
      if (authError) throw authError;
      if (authData.user) {
        const { error: profileError } = await supabase.from('profiles').update({ role: newUser.role }).eq('id', authData.user.id);
        if (profileError) throw profileError;
      }
      toast.success('用戶新增成功！');
      setNewUser({ email: '', password: '', role: 'user' });
      fetchInitialData();
    } catch (error: any) { toast.error(`新增用戶失敗: ${error.message}`); } 
    finally { setLoading(false); }
  };

  const handleToggleUserStatus = async (userId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase.from('profiles').update({ is_active: !currentStatus }).eq('id', userId);
      if (error) throw error;
      toast.success(`用戶狀態已更新為 ${!currentStatus ? '啟用' : '停用'}`);
      fetchInitialData();
    } catch (error: any) { toast.error(`更新用戶狀態失敗: ${error.message}`); }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin': return <Badge variant="destructive">admin</Badge>;
      case 'manager': return <Badge className="bg-blue-500">manager</Badge>;
      default: return <Badge variant="secondary">user</Badge>;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-background border-border text-foreground">
        <DialogHeader><DialogTitle className="text-xl font-bold text-foreground">{t('adminSettings') || '管理員設定'}</DialogTitle></DialogHeader>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-muted/50">
            <TabsTrigger value="users" className="data-[state=active]:bg-background data-[state=active]:text-foreground"><Users className="w-4 h-4 mr-2" />用戶管理</TabsTrigger>
            <TabsTrigger value="roles" className="data-[state=active]:bg-background data-[state=active]:text-foreground"><Shield className="w-4 h-4 mr-2" />角色分配</TabsTrigger>
            <TabsTrigger value="companies" className="data-[state=active]:bg-background data-[state=active]:text-foreground"><Building2 className="w-4 h-4 mr-2" />公司標誌</TabsTrigger>
          </TabsList>
          <TabsContent value="users" className="p-4 space-y-6">
            <Card className="bg-card/80 border-border">
              <CardHeader><CardTitle className="text-foreground">新增用戶</CardTitle></CardHeader>
              <CardContent className="pt-6 flex items-end gap-4">
                <div className="flex-grow space-y-2"><Label className="text-foreground">電子郵件</Label><Input type="email" placeholder="user@example.com" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} className="bg-background/50 border-input text-foreground" /></div>
                <div className="flex-grow space-y-2"><Label className="text-foreground">密碼</Label><Input type="password" placeholder="********" value={newUser.password} onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))} className="bg-background/50 border-input text-foreground" /></div>
                <div className="space-y-2"><Label className="text-foreground">角色</Label>
                  <Select value={newUser.role} onValueChange={(role: 'user' | 'manager') => setNewUser(p => ({ ...p, role }))}>
                    <SelectTrigger className="w-40 bg-background/50 border-input text-foreground"><SelectValue placeholder="選擇角色" /></SelectTrigger>
                    <SelectContent className="bg-popover border-border text-foreground"><SelectItem value="user">一般用戶</SelectItem><SelectItem value="manager">管理者</SelectItem></SelectContent>
                  </Select>
                </div>
                <Button onClick={handleAddUser} disabled={loading} className="bg-purple-600 hover:bg-purple-700 text-white"><Plus className="w-4 h-4 mr-2" />新增用戶</Button>
              </CardContent>
            </Card>
            <Card className="bg-card/80 border-border">
              <CardHeader><CardTitle className="text-foreground">用戶列表</CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-border">
                      <TableHead className="text-muted-foreground">電子郵件</TableHead>
                      <TableHead className="text-muted-foreground">角色</TableHead>
                      <TableHead className="text-muted-foreground">狀態</TableHead>
                      <TableHead className="text-muted-foreground">公司權限</TableHead>
                      <TableHead className="text-muted-foreground text-right">操作</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map(user => (
                      <TableRow key={user.id} className="border-border hover:bg-accent/50">
                        <TableCell className="text-foreground">{user.email}</TableCell>
                        <TableCell>{getRoleBadge(user.role)}</TableCell>
                        <TableCell><Badge variant={user.is_active ? "default" : "outline"}>{user.is_active ? '啟用' : '停用'}</Badge></TableCell>
                        <TableCell className="flex flex-wrap gap-1">
                          {user.role === 'admin' ? <Badge variant="secondary">所有權限</Badge> : user.company_ids.map(id => <Badge key={id} variant="secondary">{companyMap.get(id) || id}</Badge>)}
                        </TableCell>
                        <TableCell className="text-right">
                          {user.role !== 'admin' && <Button variant="outline" size="sm" onClick={() => handleToggleUserStatus(user.id, user.is_active)} className="bg-card/50 border-border text-foreground hover:bg-secondary/50">{user.is_active ? '停用' : '啟用'}</Button>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="roles" className="p-4 space-y-4">
            {users.map((user) => (
              <Card key={user.id} className="bg-card/80 border-border">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <p className="text-lg font-semibold text-foreground">{user.email}</p>
                    {getRoleBadge(user.role)}
                  </div>
                </CardHeader>
                <CardContent>
                  <Label className="text-muted-foreground mb-2 block">公司權限</Label>
                  {user.role === 'admin' ? (
                    <p className="text-muted-foreground text-sm">管理員預設擁有所有公司權限。</p>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {[...companies].sort((a, b) => String(a.id).localeCompare(String(b.id))).map((company) => (
                        <div key={company.id} className="flex items-center space-x-2 p-2 bg-muted/50 rounded-md">
                          <input
                            type="checkbox"
                            id={`${user.id}-${company.id}`}
                            checked={user.company_ids.includes(String(company.id))}
                            onChange={async () => {
                              const hasAccess = user.company_ids.includes(String(company.id));
                              const newCompanyIds = hasAccess
                                ? user.company_ids.filter(id => id !== String(company.id))
                                : [...user.company_ids, String(company.id)];
                              try {
                                const { error } = await supabase.from('profiles').update({ company_ids: newCompanyIds }).eq('id', user.id);
                                if (error) throw error;
                                toast.success('公司權限已更新');
                                fetchInitialData();
                              } catch (error: any) {
                                toast.error(`更新公司權限失敗: ${error.message}`);
                              }
                            }}
                          />
                          <Label htmlFor={`${user.id}-${company.id}`} className="text-sm font-medium leading-none text-foreground cursor-pointer">{company.name}</Label>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </TabsContent>
          <TabsContent value="companies" className="p-4 space-y-4">
            {[...companies].sort((a, b) => String(a.id).localeCompare(String(b.id))).map((company) => (
              <Card key={company.id} className="bg-card/80 border-border flex flex-col md:flex-row items-center justify-between p-4">
                <div className="flex items-center gap-4 w-full md:w-auto">
                  <img
                    src={company.logo_url || '/placeholder.svg'}
                    alt={company.name}
                    className="h-16 w-16 rounded bg-muted object-cover border border-border"
                  />
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    id={`logo-upload-${company.id}`}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const filePath = `company-logos/${company.id}-${Date.now()}-${file.name}`;
                      try {
                        const { error: uploadError } = await supabase.storage.from('logos').upload(filePath, file);
                        if (uploadError) throw uploadError;
                        const { data: urlData } = supabase.storage.from('logos').getPublicUrl(filePath);
                        if (!urlData) throw new Error('無法取得檔案公開 URL');
                        const { error: updateError } = await supabase.from('companies').update({ logo_url: urlData.publicUrl }).eq('id', company.id);
                        if (updateError) throw updateError;
                        toast.success('公司標誌已更新');
                        fetchInitialData();
                      } catch (error: any) {
                        toast.error(`上傳失敗: ${error.message}`);
                      }
                    }}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById(`logo-upload-${company.id}`)?.click()}
                    className="bg-card/50 border-border text-foreground hover:bg-secondary/50"
                  >更換標誌</Button>
                </div>
                <div className="flex items-center gap-2 mt-4 md:mt-0 w-full md:w-auto">
                  <Input
                    className="bg-background/50 border-input text-foreground"
                    value={company.name}
                    onChange={async (e) => {
                      const newName = e.target.value;
                      try {
                        const { error } = await supabase.from('companies').update({ name: newName }).eq('id', company.id);
                        if (error) throw error;
                        toast.success('公司名稱已更新');
                        fetchInitialData();
                      } catch (error: any) {
                        toast.error(`更新公司名稱失敗: ${error.message}`);
                      }
                    }}
                  />
                </div>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
        {loading && <div className="absolute inset-0 bg-black/30 flex justify-center items-center"><Loader2 className="w-8 h-8 animate-spin text-purple-500" /></div>}
        <DialogFooter className="mt-4"><Button onClick={() => onOpenChange(false)} variant="outline">關閉</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminSettingsDialog;
